import normalizeApiResponse from 'utils/normalizeApiResponse';
import get from 'lodash/get';
import toString from 'lodash/toString';
import debounce from 'lodash/debounce';
import Logger from 'observability/Logger';

import { getFormTemplateLibraryApiClient } from 'lib/SplashApiClient/subclients/FormTemplateLibraryApiClient';

import { formTemplatesSchema, ENTITIES_KEY_FORM_TEMPLATE } from 'common/entities/form';

import { notifyError } from 'common/compounds/Notification/modules';

import { isFormTypeTicketSale } from 'common/entities/form/selectors';
import { OWNER_EVENT, OWNER_TICKET_SALE_TYPE } from 'common/compounds/FormBuilder/constants/ownerTypes';
import { getFormId } from '../selectors';

import {
    OPENED_FORM_TEMPLATE_LIBRARY,
    CLOSED_FORM_TEMPLATE_LIBRARY,
    SET_FORM_TEMPLATE_LIBRARY_VIEW,
    SET_LOADING_FORM_TEMPLATE_LIBRARY,
    ADD_CUSTOM_TEMPLATE,
    ADD_CUSTOM_TEMPLATES,
    SET_STOCK_TEMPLATES,
    SET_LOADING_CUSTOM_TEMPLATES_NEXT_PAGE,
    RESET_FORM_TEMPLATES,
    REMOVE_QUESTION_FROM_CUSTOM_TEMPLATES,
    RESET_CUSTOM_TEMPLATES,
    SET_CUSTOM_TEMPLATES_SEARCH_TERM,
    SET_STOCK_TEMPLATES_SEARCH_TERM,
    SET_SELECTED_TEMPLATE_ID,
} from '../constants/actionTypes';

import {
    getStockTemplates,
    getIsLoadingTemplateLibrary,
    getLastPageLoaded,
    getTotalPages,
    getIsLoadingNextPage,
    getCustomSearchTerm,
    getSelectedTemplateId,
    getCustomTemplatesEntities,
} from '../selectors/templateLibrary';

const LOG = new Logger('templateLibrary');

// Error messages
const LOADING_QUESTIONS_ERROR_MESSAGE = 'Error loading questions. Please try again.';
const LOADING_MORE_QUESTIONS_ERROR_MESSAGE = 'Error loading more questions.';

const apiClient = getFormTemplateLibraryApiClient();

/**
 * Opens the Form Template Library
 */
export const openFormTemplateLibrary = (mode, isAdditional = false, nodeGroupId) => ({
    type: OPENED_FORM_TEMPLATE_LIBRARY,
    payload: {
        mode,
        isAdditional,
        nodeGroupId,
    },
});

/**
 * Closes the Form Template Library
 */
export const closeFormTemplateLibrary = () => ({
    type: CLOSED_FORM_TEMPLATE_LIBRARY,
});

export const removeQuestionFromCustomTemplates = (id) => ({
    type: REMOVE_QUESTION_FROM_CUSTOM_TEMPLATES,
    payload: id,
});

export const addQuestionToCustomTemplates = (id) => ({
    type: ADD_CUSTOM_TEMPLATE,
    payload: id,
});

const setCustomTemplatesSearchTerm = (customSearchTerm) => ({
    type: SET_CUSTOM_TEMPLATES_SEARCH_TERM,
    payload: customSearchTerm,
});

export const setStockTemplatesSearchTerm = (stockSearchTerm) => ({
    type: SET_STOCK_TEMPLATES_SEARCH_TERM,
    payload: stockSearchTerm,
});

export const setSelectedTemplateId = (id) => ({
    type: SET_SELECTED_TEMPLATE_ID,
    payload: id,
});

const setLoadingFormTemplateLibrary = (isLoading) => ({
    type: SET_LOADING_FORM_TEMPLATE_LIBRARY,
    payload: {
        isLoading,
    },
});

const resetFormTemplates = () => ({
    type: RESET_FORM_TEMPLATES,
});

const resetCustomTemplates = {
    type: RESET_CUSTOM_TEMPLATES,
};

/**
 * Sets the current view of the Form Template Library and resets template selection.
 */
export const setFormTemplateLibraryView = (activeTab) => ({
    type: SET_FORM_TEMPLATE_LIBRARY_VIEW,
    activeTab,
});

const setStockTemplates = (stockTemplates) => {
    const normStockTemplates = normalizeApiResponse(stockTemplates, formTemplatesSchema);
    const newStockEntities = get(normStockTemplates, `entities.${ENTITIES_KEY_FORM_TEMPLATE}`);
    return {
        type: SET_STOCK_TEMPLATES,
        payload: {
            entities: {
                [ENTITIES_KEY_FORM_TEMPLATE]: newStockEntities,
            },
        },
    };
};

const addCustomTemplates = (customTemplates, pagination) => {
    const normCustomTemplates = normalizeApiResponse(customTemplates, formTemplatesSchema);
    const newCustomEntities = get(normCustomTemplates, `entities.${ENTITIES_KEY_FORM_TEMPLATE}`);

    return {
        type: ADD_CUSTOM_TEMPLATES,
        payload: {
            entities: {
                [ENTITIES_KEY_FORM_TEMPLATE]: newCustomEntities,
            },
            totalPages: get(pagination, 'pages'),
            lastPageLoaded: get(pagination, 'page'),
            templates: customTemplates.map((template) => toString(template.id)),
        },
    };
};

export const fetchApiQuestions = (dispatch, getState) => {
    const state = getState();
    const formId = getFormId(state);
    const formType = isFormTypeTicketSale(state, formId) ? OWNER_TICKET_SALE_TYPE : OWNER_EVENT;

    dispatch(setLoadingFormTemplateLibrary(true));
    dispatch(resetFormTemplates());

    return Promise.all([apiClient.getCustomFormTemplates(), apiClient.getStockFormTemplates(formType)])
        .then(([{ templates: customTemplates, pagination }, { templates: stockTemplates }]) => {
            dispatch(setStockTemplates(stockTemplates));

            dispatch(addCustomTemplates(customTemplates, pagination));

            dispatch(setLoadingFormTemplateLibrary(false));
        })
        .catch((error) => {
            dispatch(notifyError(LOADING_QUESTIONS_ERROR_MESSAGE));
            LOG.error('Error on fetchApiQuestions', error);
            dispatch(setLoadingFormTemplateLibrary(false));
            throw error;
        });
};

const setLoadingCustomTemplatesNextPage = (isLoading) => ({
    type: SET_LOADING_CUSTOM_TEMPLATES_NEXT_PAGE,
    payload: {
        isLoading,
    },
});

export const loadNextCustomTemplatesPage = () => (dispatch, getState) => {
    const state = getState();
    const lastPageLoaded = getLastPageLoaded(state);
    const totalPages = getTotalPages(state);
    const isLoadingNextPage = getIsLoadingNextPage(state);
    const customSearchTerm = getCustomSearchTerm(state);

    // Safety check.
    if (isLoadingNextPage || lastPageLoaded >= totalPages) {
        return;
    }

    dispatch(setLoadingCustomTemplatesNextPage(true));

    apiClient
        .getCustomFormTemplates(lastPageLoaded + 1, customSearchTerm)
        .then(({ templates: customTemplates, pagination }) => {
            dispatch(addCustomTemplates(customTemplates, pagination));
            dispatch(setLoadingCustomTemplatesNextPage(false));
        })
        .catch(() => {
            dispatch(notifyError(LOADING_MORE_QUESTIONS_ERROR_MESSAGE));
            dispatch(setLoadingCustomTemplatesNextPage(false));
        });
};

/**
 * Loads questions from the API
 */
export const loadApiQuestions =
    (force = false) =>
    (dispatch, getState) => {
        const state = getState();
        const questions = getStockTemplates(state);
        // Check for previous questions loaded
        const areQuestionsLoaded = questions && Object.keys(questions).length > 0;
        // Check if questions request is pending
        const formQuestionLoading = getIsLoadingTemplateLibrary(state);

        if (formQuestionLoading || areQuestionsLoaded) {
            return null;
        }

        return dispatch(fetchApiQuestions);
    };

export const removeQuestion = (question) => (dispatch, getState) => {
    const state = getState();
    const questionId = question.id;
    const selectedTemplateId = getSelectedTemplateId(state);

    dispatch(removeQuestionFromCustomTemplates(questionId));
    questionId === selectedTemplateId && dispatch(setSelectedTemplateId(null));

    apiClient.removeQuestionFromFormTemplateLibrary(questionId).catch((err) => {
        dispatch(notifyError("Sorry, we couldn't remove this question."));
        dispatch(addQuestionToCustomTemplates(questionId));
        LOG.error('Error on removeQuestion', err);
    });
};

const getCustomTemplatesThunk = (page, customSearchTerm) => (dispatch) => {
    apiClient
        .getCustomFormTemplates(page, customSearchTerm)
        .then(({ templates: customTemplates, pagination }) => {
            dispatch(setLoadingCustomTemplatesNextPage(false));
            dispatch(addCustomTemplates(customTemplates, pagination));
        })
        .catch((err) => {
            dispatch(notifyError('Sorry, there has been a problem with your search.'));
            dispatch(setLoadingCustomTemplatesNextPage(false));
        });
};

const getCustomTemplatesThunkDebounced = debounce(
    (page, searchTerm, dispatch) => getCustomTemplatesThunk(page, searchTerm)(dispatch),
    1000,
);

export const searchCustomQuestion = (customSearchTerm) => (dispatch, getState) => {
    const state = getState();
    const previousCustomSearchTerm = getCustomSearchTerm(state);
    const customTemplateEntities = getCustomTemplatesEntities(state);

    dispatch(setCustomTemplatesSearchTerm(customSearchTerm));

    if (customTemplateEntities.length > 0 && previousCustomSearchTerm.trim() !== customSearchTerm.trim()) {
        dispatch(resetCustomTemplates);
        dispatch(setLoadingCustomTemplatesNextPage(true));
        getCustomTemplatesThunkDebounced(0, customSearchTerm, dispatch);
    }
};
